import { PreviewContact } from "@/schema"
import { ButtonBadge } from "@ui/button-badge"
import { Badge } from "@ui/shad"
import { ExternalLink, Tag, User } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link } from "@/components/book-link"
import { PhoneBadgeWithRingout } from "@/components/tables/phone-badge-with-ringout"

import PeekViewSkeleton from "./skeleton"

export default function ContactPeekView({ contactId, href }: { contactId: string; href: string | null }) {
  const { data: contact, isLoading } = usePeekViewData(contactId, "Contact") as {
    data: PreviewContact
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton />

  const contactName = contact.person?.full_name || "Unnamed contact"

  return (
    <div className="flex w-xs flex-col items-stretch gap-2 xl:w-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline">
          <p className="truncate font-medium">{contactName}</p>
          {href && (
            <Link
              href={href}
              className="text-muted-foreground hover:text-link ml-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open in new page"
            >
              <ExternalLink className="size-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        <Badge variant="outline" className="flex-none">
          Contact
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <User className="text-icon size-4 flex-none" />
        {contact.email && (
          <ButtonBadge text={contact.email || ""} copyText={contact.email || ""} className="max-w-1/2 truncate" />
        )}
        {contact.phone_number && (
          <PhoneBadgeWithRingout
            value={contact.phone_number}
            extension={contact.phone_number_extension}
            name={contactName}
          />
        )}
      </div>
      {contact.tags && contact.tags.length > 0 && (
        <div className="flex items-center gap-2 truncate">
          <Tag className="text-icon size-4 flex-none" />
          {contact.tags.slice(0, 3).map((tag) => (
            <Badge key={tag.id} variant="secondary" className="flex-none">
              {tag.name}
            </Badge>
          ))}
          {contact.tags.length > 3 && (
            <Badge variant="secondary" className="flex-none">
              +{contact.tags.length - 3}
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}
